import { useQuery } from '@apollo/client';
import { Box, Checkbox, IconButton, InputAdornment, styled } from '@mui/material';
import { Search } from '@styled-icons/bootstrap';
import { Close } from '@styled-icons/ionicons-sharp';
import { Loader } from 'components/Forms';
import { SearchInput } from 'components/Forms/SearchToolbar';
import { useDialog } from 'components/hooks';
import { EmptySearchResults, ErrorState, InfiniteScrollWrapper } from 'components/Structure';
import React, { FC, useMemo, useState } from 'react';
import { GraphQlConnection } from 'types/types';
import { removeGraphConnections } from 'utils/graphql.utils';
import DatasetItem from './GeoDataItem';
import { EGeoDataStatus } from 'types/geodata.types';
import { GET_DATASETS } from 'graphql/secure/queries/dataset.queries';
import { IDataset } from 'types/dataset.types';

interface IDatasetSelectionProps {
  selectedDatasetIds: string[];
  setSelectedDatasetIds: (ids: string[]) => void;
}

const CloseButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.primary,
  padding: 0,
  margin: 0,
}));

const DatasetSelection: FC<IDatasetSelectionProps> = ({
  selectedDatasetIds,
  setSelectedDatasetIds,
}) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const { openDialog } = useDialog();

  const { data, loading, error, fetchMore } = useQuery<{ datasets: GraphQlConnection<IDataset> }>(
    GET_DATASETS,
    {
      variables: {
        filters: {
          searchTerm,
          eudrRelevant: true,
          status: [
            EGeoDataStatus.VERIFICATION_FAILED,
            EGeoDataStatus.COMPLETED,
            EGeoDataStatus.NORMALISED,
          ],
        },
        first: 15,
      },
    }
  );

  const hasNextPage: boolean = !!data?.datasets?.pageInfo?.hasNextPage;
  const endCursor: string | undefined = data?.datasets?.pageInfo?.endCursor;

  const datasets = useMemo(
    () => (data?.datasets ? removeGraphConnections(data?.datasets) : []),
    [data]
  );

  const handlePageEndReached = () => {
    if (endCursor) {
      fetchMore({
        variables: {
          after: endCursor,
        },
      });
    }
  };

  const handleSelect = (id: string) => {
    const isSelected = selectedDatasetIds.includes(id);
    if (!isSelected) {
      setSelectedDatasetIds([...selectedDatasetIds, id]);
    } else {
      setSelectedDatasetIds(selectedDatasetIds.filter(datasetId => datasetId !== id));
    }
  };

  const handleToggleAll = () => {
    if (selectedDatasetIds.length === datasets.length) {
      setSelectedDatasetIds([]);
    } else {
      setSelectedDatasetIds(datasets.map(({ id }) => id));
    }
  };

  const handleViewPlots = (dataset: IDataset) => {
    openDialog({
      type: 'GEO_DATA_PLOT_OVERVIEW',
      props: { dataset },
    });
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1}>
        <Checkbox
          checked={selectedDatasetIds.length === data?.datasets.edges.length}
          onClick={handleToggleAll}
        />
        <SearchInput
          setDebouncedState={setSearchTerm}
          className="search-input"
          data-cy="dataset-search-input"
          delay={500}
          placeholder="Search"
          initialValue={searchTerm}
          inputProps={{
            endAdornment: (
              <InputAdornment position="start">
                {searchTerm === '' ? (
                  <Search size={12} />
                ) : (
                  <CloseButton onClick={() => setSearchTerm('')}>
                    <Close size={16} />
                  </CloseButton>
                )}
              </InputAdornment>
            ),
          }}
        />
      </Box>
      {loading && (
        <Box position="relative" height="100%" p={5}>
          <Loader />
        </Box>
      )}

      {error && (
        <Box position="relative" height="100%" p={10}>
          <ErrorState error={error} />
        </Box>
      )}

      {!loading && datasets.length === 0 && (
        <Box position="relative" p={15}>
          <EmptySearchResults skeletonComponent={searchTerm} />
        </Box>
      )}
      {!loading && datasets.length > 0 && (
        <InfiniteScrollWrapper hasMore={hasNextPage} next={handlePageEndReached}>
          <Box display="flex" width="100%" flexDirection="column" gap={1} pt={2}>
            {datasets.map(dataset => {
              const isSelected = selectedDatasetIds.includes(dataset.id);

              return (
                <Box display="flex" alignItems="center" key={dataset.id}>
                  <Box mr={1}>
                    <Checkbox checked={isSelected} onClick={() => handleSelect(dataset.id)} />
                  </Box>
                  <DatasetItem
                    title={dataset.title}
                    rawMaterialTitle={dataset.rawMaterial.title}
                    countryCode={dataset.originCountry}
                    partnerTitle={dataset.ownedBy.name}
                    status={dataset.status}
                    issues={dataset.issues}
                    createdTimestamp={dataset.createdTimestamp}
                    onClick={() => handleSelect(dataset.id)}
                    onClickViewPlots={() => handleViewPlots(dataset)}
                  />
                </Box>
              );
            })}
          </Box>
        </InfiniteScrollWrapper>
      )}
    </Box>
  );
};

export default DatasetSelection;
